<script setup lang="ts">
  import { trans, transChoice } from '@procommerce/translator';
  import { $post, route } from '@procommerce/visitor';
  import { computed, ref } from 'vue';
  import { UnitFormatter } from '$support';
  import { Unit } from '$types';
  import { useVariantStock } from '$storefront';

  interface StockMeta {
    total: number;
    current: number;
    levels: Record<number, string>;
  }

  interface PackagedEntity {
    stock_unit: Unit;
    stock_unit_base: string; // DECIMAL
    package_unit: Unit;
    package_quantity: string; // DECIMAL
  }

  interface PackagedVariant {
    id: number;
    variant_id?: number;
    stock_on_order: boolean;
    stock_available: string;
    stock_meta: StockMeta | undefined;
    disable_sales_over_supply: boolean;
  }

  interface Props {
    product: PackagedEntity;
    variant: PackagedVariant;
  }

  const emit = defineEmits([
    'added',
  ]);

  const props = defineProps<Props>();
  const { available, amountAvailable } = useVariantStock(props.variant);
  const error = ref(null);
  const loading = ref(false);

  const message = computed(() => {
    let quantity = props.product.package_quantity;
    let unit = props.product.package_unit;
    let stock = props.product.stock_unit;

    if (unit === Unit.PIECE && quantity === '1.0000') {
      return undefined;
    }

    let packaging = transChoice(`UNIT.${unit}.LONG`, 1);

    return trans(':packaging zawiera: <b>:unit</b>', {
      packaging: packaging.charAt(0).toUpperCase() + packaging.slice(1),
      unit: UnitFormatter.accusative(stock, quantity),
    });
  });

  const isMultipleOf = computed(() => {
    if (props.product.package_quantity && props.product.stock_unit_base) {
      return Number(props.product.package_quantity) % Number(props.product.stock_unit_base) === 0;
    } else {
      return false;
    }
  });

  const isAvailable = computed(() => {
    return amountAvailable.value >= Number(props.product.package_quantity);
  });

  function actionAddToCart() {
    if (props.variant && props.product.package_quantity !== '1.0000') {
      loading.value = true;
      error.value = null;

      $post(route('api.v1.web.cart.item.store'), {
        variant: props.variant.variant_id || props.variant.id,
        amount: props.product.package_quantity,
      }).then((res) => {
        emit('added', res);
      }).catch((err) => {
        error.value = err.errors.amount[0];
      }).finally(() => {
        loading.value = false;
      });
    }
  }
</script>
<template>
  <div v-if="message" class="product-quantity-wrapper">
    <div class="product-pack-quantity" v-html="message" />
    <template v-if="isMultipleOf && isAvailable">
      <SfButton small primary :loading="loading" @click="actionAddToCart">
        do koszyka
      </SfButton>
    </template>
  </div>
</template>
<style scoped lang="scss">
  .product-quantity-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    width: 100%;

    .product-pack-quantity {
      font-size: 0.875rem;
      font-family: var(--font-family-sans);
      color: var(--color-default);
      font-weight: var(--font-weight-regular);
      padding: 0;
      margin: 0;
      min-width: 0;
      width: 100%;

      .contains {
        display: block;
      }
    }
  }
</style>
